import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Translate } from 'react-localize-redux';
import Chevron from '../../../resources/icons/chevron.svg';

import css from './CreativePopup.css';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { CreativeType, CreativeTypes } from '../../types';
import CreativeAsset from '../creativeAsset/CreativeAsset';
import CreativePopupDimensionTags from '../creativePopupDimensionTags/CreativeDimensionTags';
import CreativeAITagsTable from '../creativeAITagsTable/CreativeAITagsTable';
import { UNKNOWN_INDICATION } from '../../creativesFormattingUtils';
import { GeneralPopup, PopupTypes } from '../../../components/partials';

export const GO_BACK_BUTTON_TITLE = 'goBackButton';
export const CREATIVE_POPUP_TITLE = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TITLE';

export const formatValues = values => (values.length ? values.join(', ') : UNKNOWN_INDICATION);

const ASSET_ASPECT_RATIO = 550 / 400;
export const getTranslationKey = key => `STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.${key}`;

const CreativePopup = ({ app, source, creative, onClose: onCloseProp }) => {
    const assetRef = useRef();
    const [videoDuration, setVideoDuration] = useState();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showGoBackConfirmation, setShowGoBackConfirmation] = useState(false);

    const {
        assetIdList,
        assetNameList,
        creativeIdList,
        creativeNameList,
        countryList,
        campaignNameList,
    } = creative.multiValuedDimensions;
    const isAssetName = !!assetNameList?.length;
    const isAssetId = !!assetIdList?.length;

    const handleClose = useCallback(() => {
        if (creative.type === CreativeTypes.PLAYABLE && assetRef.current) assetRef.current.style.pointerEvents = 'none';
        onCloseProp();
    }, [creative.type, onCloseProp]);

    const onClose = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowGoBackConfirmation(true);
        } else {
            handleClose();
        }
    }, [hasUnsavedChanges, handleClose]);

    const handleConfirmClose = () => {
        setShowGoBackConfirmation(false);
        handleClose();
    };

    const handleCancelClose = () => {
        setShowGoBackConfirmation(false);
    };

    const closeOnEscape = useCallback(event => event.key === 'Escape' && onClose(), [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', closeOnEscape, false);
        return () => document.removeEventListener('keydown', closeOnEscape, false);
    }, [closeOnEscape]);

    useEffect(() => {
        const handleBeforeUnload = event => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = '';
                alert('You have unsaved changes. Are you sure you want to leave?');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const onLoad = () => {
        if (creative.type === CreativeTypes.VIDEO) setVideoDuration(assetRef.current?.duration);
    };

    return (
        <div className={css.creativePopup}>
            <GeneralPopup
                open={showGoBackConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmClose}
                onReject={handleCancelClose}
            />
            <div className={css.header}>
                <div className={css.goBackContainer} onClick={onClose}>
                    <Chevron title={GO_BACK_BUTTON_TITLE} className={css.goBackIcon} />
                    <div className={css.goBackText}>
                        <Translate id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.GO_BACK" />
                    </div>
                </div>
                <div className={css.title}>
                    <Translate id={CREATIVE_POPUP_TITLE} />
                </div>
            </div>
            <div className={css.content}>
                <div className={css.creativeData}>
                    <CreativePopupDimensionTags
                        creative={creative}
                        source={source}
                        app={app}
                        videoDuration={videoDuration}
                    />
                    <CreativeAsset
                        ref={assetRef}
                        type={creative.type}
                        url={creative.url}
                        embeddingUrl={creative.embeddingUrl}
                        className={css.creativeAsset}
                        controls
                        aspectRatio={ASSET_ASPECT_RATIO}
                        onLoad={onLoad}
                        muted={false}
                    />
                    <div className={css.creativeRawData}>
                        <Translate
                            id={getTranslationKey(`${isAssetName ? 'ASSET' : 'CREATIVE'}_NAME`)}
                            data={{ value: formatValues(isAssetName ? assetNameList : creativeNameList || []) }}
                        />
                        <Translate
                            id={getTranslationKey(`${isAssetId ? 'ASSET' : 'CREATIVE'}_ID`)}
                            data={{ value: formatValues(isAssetId ? assetIdList : creativeIdList || []) }}
                        />
                        <Translate
                            id={getTranslationKey('CAMPAIGN_NAME')}
                            data={{ value: formatValues(campaignNameList || []) }}
                        />
                        <Translate
                            id={getTranslationKey('COUNTRY')}
                            data={{ value: formatValues(countryList || []) }}
                        />
                    </div>
                </div>
                <CreativeAITagsTable
                    app={app.value}
                    source={source?.label}
                    imageHash={creative.imageHash}
                    onHasUnsavedChanges={setHasUnsavedChanges}
                />
            </div>
        </div>
    );
};

CreativePopup.propTypes = {
    creative: PropTypes.shape(CreativeType).isRequired,
    app: AutoCompleteOptionType.isRequired,
    source: AutoCompleteOptionType.isRequired,
    onClose: PropTypes.func,
};

export default CreativePopup;
